export const BROADSIGHT_PRIMARY_BLUE = '#1F68C1';
export const BROADSIGHT_SECONDARY_BLUE = '#2D99F4';
export const ANALYTICS_SURFACE = '#FFFFFF';
export const ANALYTICS_TEXT_PRIMARY = '#2B2B2B';
export const ANALYTICS_TEXT_MUTED = '#666666';
export const ANALYTICS_TEXT_SUBTLE = 'rgba(0, 0, 0, 0.60)';
export const ANALYTICS_CARD_BORDER = 'rgba(177, 185, 201, 0.85)';
export const ANALYTICS_CARD_BORDER_SOFT = 'rgba(177, 185, 201, 0.75)';
export const ANALYTICS_CARD_SHADOW = '0 6px 18px rgba(17, 32, 61, 0.08)';
export const ANALYTICS_POPOVER_BORDER = 'rgba(0, 0, 0, 0.10)';
export const ANALYTICS_POPOVER_SHADOW = '0 10px 28px rgba(0, 0, 0, 0.16)';
export const ANALYTICS_DIVIDER = 'rgba(0, 0, 0, 0.08)';
export const ANALYTICS_DOT = 'rgba(0, 0, 0, 0.35)';
export const ANALYTICS_ICON_HOVER = 'rgba(0, 0, 0, 0.06)';

export const BROADSIGHT_BLUE_PALETTE = [
  '#1F68C1',
  '#2D99F4',
  '#4BAAF7',
  '#6CB9FA',
  '#8BC7FC',
  '#A8D6FD',
  '#C4E4FE',
  '#E0F1FF',
];

export const BROADSIGHT_GREEN_PALETTE = ['#5FBA7D', '#1E8E4D', '#A1D5B2'];
export const BROADSIGHT_ORANGE_YELLOW_PALETTE = ['#ED721F', '#F19E26', '#F3BC2F', '#F6D15B'];

// Alternates hue families to keep neighboring series visually distinct.
export const CUSTOM_ANALYTICS_ROTATING_PALETTE = [
  '#174AA2',
  '#5FBA7D',
  '#ED721F',
  '#1F68C1',
  '#F3BC2F',
  '#1E8E4D',
  '#2D99F4',
  '#F19E26',
  '#0055B7',
  '#4BAAF7',
];

// Controlled subset for readability (no very light tints for small words).
export const CUSTOM_ANALYTICS_WORD_CLOUD_PALETTE = ['#174AA2', '#1F68C1', '#2D99F4', '#5FBA7D', '#ED721F', '#F19E26'];

export const BROADSIGHT_STATUS_COLORS = {
  'In Progress': '#E2F6CA',
  Completed: '#ABF5D1',
  'Participation Ribbon': '#BFD7FF',
  Participation: '#BFD7FF',
  Cancelled: '#C0B6F2',
  'AutoTracker Draft': '#f6fd00',
};

export const BROADSIGHT_COMPLEXITY_COLORS = {
  1: '#FBEAB6',
  2: '#F6D15B',
  3: '#F3BC2F',
  4: '#F19E26',
  5: '#ED721F',
};

export const BROADSIGHT_TONE_COLORS = {
  Positive: '#5FBA7D',
  'Neutral or mixed': '#707070',
  Negative: '#F15B50',
  'N/A': '#174AA2',
};

const hexToRgb = (hexColor) => {
  const normalized = hexColor.replace('#', '');
  const value =
    normalized.length === 3
      ? normalized
          .split('')
          .map((c) => `${c}${c}`)
          .join('')
      : normalized;
  const parsed = Number.parseInt(value, 16);

  if (Number.isNaN(parsed)) {
    return null;
  }

  return {
    r: (parsed >> 16) & 255,
    g: (parsed >> 8) & 255,
    b: parsed & 255,
  };
};

export const withAlpha = (hexColor, alpha) => {
  const rgb = hexToRgb(hexColor);
  if (!rgb) {
    return hexColor;
  }
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
};

export const getBlueSeriesColors = (index) => {
  const color = BROADSIGHT_BLUE_PALETTE[index % BROADSIGHT_BLUE_PALETTE.length];
  return {
    border: color,
    background: withAlpha(color, 0.24),
    hover: withAlpha(color, 0.44),
  };
};

export const getBluePaletteColor = (index) => {
  return BROADSIGHT_BLUE_PALETTE[index % BROADSIGHT_BLUE_PALETTE.length];
};

export const getCustomAnalyticsColor = (index, context = {}) => {
  const semanticColor = context?.label ? getSemanticSeriesColor(context.label) : null;
  if (semanticColor) {
    return semanticColor;
  }
  return CUSTOM_ANALYTICS_ROTATING_PALETTE[index % CUSTOM_ANALYTICS_ROTATING_PALETTE.length];
};

export const getCustomAnalyticsSeriesColors = (index, context = {}) => {
  const color = getCustomAnalyticsColor(index, context);
  return {
    border: color,
    background: withAlpha(color, 0.24),
    hover: withAlpha(color, 0.44),
  };
};

export const getAnalyticsPaletteColor = (index, options = {}) => {
  const { variant = 'default', label } = options;
  if (variant === 'custom') {
    return getCustomAnalyticsColor(index, { label });
  }
  return getBluePaletteColor(index);
};

export const getAnalyticsSeriesColors = (index, options = {}) => {
  const { variant = 'default', label } = options;
  if (variant === 'custom') {
    return getCustomAnalyticsSeriesColors(index, { label });
  }
  return getBlueSeriesColors(index);
};

export const getWordCloudColor = (index, options = {}) => {
  const { variant = 'default' } = options;
  if (variant === 'custom') {
    return CUSTOM_ANALYTICS_WORD_CLOUD_PALETTE[index % CUSTOM_ANALYTICS_WORD_CLOUD_PALETTE.length];
  }
  return getBluePaletteColor(index);
};

export const getStatusSemanticColor = (label) => {
  if (typeof label !== 'string') {
    return null;
  }

  const normalized = label.trim();
  return BROADSIGHT_STATUS_COLORS[normalized] || null;
};

const parseComplexityLevel = (label) => {
  if (typeof label === 'number' && label >= 1 && label <= 5) {
    return label;
  }
  if (typeof label !== 'string') {
    return null;
  }

  const normalized = label.trim().toLowerCase();
  const leadingDigitMatch = /^([1-5])(?:\b|\s*[-:])/.exec(normalized);
  if (leadingDigitMatch) {
    return Number(leadingDigitMatch[1]);
  }

  const exactNumericMatch = /^([1-5])$/.exec(normalized);
  if (exactNumericMatch) {
    return Number(exactNumericMatch[1]);
  }

  return null;
};

export const getComplexitySemanticColor = (label) => {
  const level = parseComplexityLevel(label);
  if (!level) {
    return null;
  }
  return BROADSIGHT_COMPLEXITY_COLORS[level] || null;
};

const parseToneLabel = (label) => {
  if (typeof label !== 'string') {
    return null;
  }
  const normalized = label.trim().toLowerCase();
  if (normalized === 'positive') {
    return 'Positive';
  }
  if (normalized === 'negative') {
    return 'Negative';
  }
  if (normalized === 'neutral' || normalized === 'neutral or mixed') {
    return 'Neutral or mixed';
  }
  if (normalized === 'n/a' || normalized === 'na' || normalized === 'not applicable') {
    return 'N/A';
  }
  return null;
};

export const getToneSemanticColor = (label) => {
  const tone = parseToneLabel(label);
  if (!tone) {
    return null;
  }
  return BROADSIGHT_TONE_COLORS[tone] || null;
};

export const getSemanticSeriesColor = (label) => {
  return getStatusSemanticColor(label) || getComplexitySemanticColor(label) || getToneSemanticColor(label);
};
